import { cloneDeep, isEqual } from 'lodash';
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useReducer
} from 'react';
import { connect } from 'react-redux';
import useSkipUpdate from '../../hooks/useSkipRender';
import { LOAD_FORM } from '../../store/modules/state/formBuilder';
import fetchAPI from '../../utils/fetchAPI';
import log from '../../utils/log';
import FormBuilderView from '../view/FormBuilderView';
import useAutosaveStatus from '../../hooks/useAutosaveStatus';
import { useHistory } from 'react-router';

export const FormBuilderContext = React.createContext({});

const FormBuilderWizardReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'set_rules':
      return { ...state, rules: payload };
    case 'set_reminders':
      return { ...state, reminders: payload };
    // case 'set_form':
    //   return { ...state, form: payload };
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const initialState = {
  rules: [],
  fieldsUsedInCalculations: [],
  reminders: []
  // form: {}
};

const FormBuilderEngine = ({
  dispatch,
  pageId,
  formId,
  form: stateForm,
  pathname,
  previousLocation
}) => {
  const history = useHistory();
  const form = useMemo(() => stateForm || {}, [stateForm]);
  const [data, updateData] = useReducer(FormBuilderWizardReducer, initialState);

  const [isOverviewDialogOpen, setIsOverviewDialogOpen] = useState(false);
  const [currentStage, setCurrentStage] = useState('edit');
  const [lastStage, setLastStage] = useState('');
  const [hasEntries, setHasEntries] = useState(false); // to allow toggle of admin fields
  const [externalUsersLicense, setExternalUsersLicense] = useState(false); // to allow toggle of admin fields
  const [menuWidth, setMenuWidth] = useState('');
  const [startingFolder, setStartingFolder] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const { shouldSkipUpdate, skipUpdate } = useSkipUpdate();
  const autosaveStatus = useAutosaveStatus(`state.formBuilder.${formId}`); // Don't dare to remove this!!! IT WILL BREAK THE FORM BUILDER!!! ¯\_(ツ)_/¯

  const bucketNames = React.useRef({});
  const bucketFields = React.useRef({});

  // useEffect(() => {
  //   if (shouldSkipUpdate()) {
  //     return;
  //   }
  //   if (JSON.stringify(form) !== JSON.stringify(data.form)) {
  //     const newForm = cloneDeep(form);
  //     updateData({ type: 'set_form', payload: newForm });
  //   }
  // }, [form]);

  useEffect(() => {
    if (lastStage && currentStage !== lastStage && currentStage === 'edit') {
      setLastStage(currentStage);
      // refresh reminders
      fetchAPI(
        `/scheduler/v1/schedule` +
          `?sort='metadata'->'createdDate' asc` +
          `&query=[[{"field": "'assetId'", "op": "=", "value": "${formId}" }, {"field": "'jobType'", "op": "=", "value": "form_reminders" }]]`
      )
        .then(res => {
          if (res?.results) {
            updateData({ type: 'set_reminders', payload: res.results });
          }
        })
        .catch(() => {});
    } else {
      setLastStage(currentStage);
    }
  }, [currentStage]);

  useEffect(() => {
    const [pathBase, formId, screen] = pathname.split('/').slice(1);
    const isNew = formId === 'new';
    const allStages = [
      'edit',
      'statuses',
      'automations',
      'collaborate',
      'reminders',
      'integrations',
      'notifications',
      'share',
      'template'
    ];

    if (isNew) {
      setIsOverviewDialogOpen(true);
    } else if (screen && allStages.indexOf(screen) !== -1) {
      setCurrentStage(screen);
    }

    const getEditPermissionPromise = new Promise((resolve, reject) => {
      if (formId === 'new') {
        resolve();
        return;
      }
      fetchAPI(`/forms/v1/forms/${formId}/checkout`)
        .then(res => {
          resolve();
        })
        .catch(e => {
          history.push('/no-access?err=link');
        });
    });

    const getFormPromise = isNew
      ? {}
      : new Promise((resolve, reject) =>
          fetchAPI(`/forms/v1/forms/${formId}`).then(resolve).catch(reject)
        );

    // get rules
    const getRulesPromise = isNew
      ? []
      : new Promise((resolve, reject) =>
          fetchAPI(
            `/forms/v1/rules?limit=10000&query=[[{"field":"'formId'","op":"=","value":"${formId}"}]]`
          )
            .then(res => resolve(res.results))
            .catch(reject)
        );

    const getRemindersPromise = isNew
      ? []
      : new Promise((resolve, reject) =>
          fetchAPI(
            `/scheduler/v1/schedule` +
              `?sort='metadata'->'createdDate' asc` +
              `&query=[[{"field": "'assetId'", "op": "=", "value": "${formId}" }, {"field": "'jobType'", "op": "=", "value": "form_reminders" }]]`
          )
            .then(res => resolve(res.results))
            .catch(reject)
        );

    const getFolderPromise = () => {
      if (isNew) {
        if (previousLocation) {
          return previousLocation.split('/')[3];
        }
        return '';
      }
      return new Promise((resolve, reject) =>
        fetchAPI(
          `/forms/v1/forms?query=[[{"field": "'id'", "op": "=", "value": "'${formId}"}]]`
        )
          .then(res => {
            if (
              res &&
              res.results &&
              res.results.length &&
              res.results[0].displayInfo &&
              res.results[0].displayInfo.folderId
            ) {
              resolve(res.results[0].displayInfo.folderId);
            } else {
              resolve('');
            }
          })
          .catch(reject)
      );
    };

    const doesFormHasSubmissionsPromise = isNew
      ? false
      : new Promise((resolve, reject) =>
          fetchAPI(`/forms/v1/forms/${formId}/submissions?limit=1`)
            .then(r => resolve(r.results.length > 0))
            .catch(reject)
        );

    const isLicensedForExternalUsersPromise = new Promise((resolve, reject) =>
      fetchAPI(
        `/configurations/v1/modules/license?order=id asc&query=[[{"field":"'module'","op":"=","value":"provisioning"},{"field":"'configName'","op":"=","value":"license"},{"field":"'code'","op":"=","value":"externalusers"}]]`
      )
        .then(res => {
          let a, n;
          a = res && res.configs && res.configs.length && res.configs[0].value;
          n = Number(a) || 0;
          resolve(!!n);
        })
        .catch(e => {})
    );

    const getLookupDependenciesPromise = isNew
      ? false
      : new Promise((resolve, reject) =>
          fetchAPI(`/forms/v1/forms/${formId}/dependencies`)
            .then(r => resolve(r))
            .catch(reject)
        );

    Promise.all([
      getEditPermissionPromise,
      getFormPromise,
      getRulesPromise,
      getFolderPromise(),
      doesFormHasSubmissionsPromise,
      isLicensedForExternalUsersPromise,
      getLookupDependenciesPromise,
      getRemindersPromise
    ])

      .then(
        ([
          permissions,
          form,
          rules,
          folderId,
          doesFormHasSubmissions,
          isLicensedForExternalUsers,
          lookupDependencies,
          reminders
        ]) => {
          updateData({
            type: 'update',
            payload: { rules, lookupDependencies, reminders }
          });
          dispatch({ type: LOAD_FORM, payload: { ...form, id: form.id } });
          setStartingFolder(folderId);
          setCurrentFolder(folderId);
          setHasEntries(doesFormHasSubmissions);
          setExternalUsersLicense(isLicensedForExternalUsers);
        }
      )
      .catch(log);

    // not sure what this is for - don't want to break anything by removing this
    // if any one find out what this is for, please update
    // add class "-is-not-dragging" before first drag
    document.body.classList.add('-is-not-dragging');
  }, []);

  const reloadForm = () => {
    return fetchAPI(`/forms/v1/forms/${formId}`).then(newFormData => {
      dispatch({
        type: LOAD_FORM,
        payload: { ...newFormData, id: newFormData.id }
      });
    });
  };

  const autosave = useCallback(
    ({ wait, payload, callback, ...action }) => {
      const formId = form.id;
      if (formId) {
        dispatch({
          autosave: {
            storePath: `state.formBuilder.${formId}`,
            endpointPath: `/forms/v1/forms/${formId}`,
            wait,
            callback
          },
          ...action,
          payload: {
            id: formId,
            ...payload
          }
        });
      }
    },
    [form, dispatch]
  );

  const viewProps = {
    currentStage,
    setCurrentStage,
    menuWidth,
    setMenuWidth,
    pageId,
    formId: formId || form.id,
    sharingType: form ? form.sharingType : '',
    isNew: !!!form.id,
    dispatch,
    autosave,
    form,
    hasEntries,
    rules: data.rules,
    setRules: rules => updateData({ type: 'set_rules', payload: rules }),
    reminders: data.reminders,
    setReminders: reminders =>
      updateData({ type: 'set_reminders', payload: reminders }),
    allLookupDependencies: data.lookupDependencies,
    startingFolder,
    currentFolder,
    setCurrentFolder,
    setIsOverviewDialogOpen,
    isOverviewDialogOpen,
    previousLocation,
    autosaveStatus,
    reloadForm
  };

  const contextProps = {
    form,
    formId: form.id || formId,
    externalUsersLicense,
    rules: data.rules,
    reminders: data.rules,
    reduxDispatch: dispatch,
    bucketNames,
    bucketFields,
    autosave,
    updateData,
    setCurrentStage
  };

  return (
    <FormBuilderContext.Provider value={contextProps}>
      <FormBuilderView {...viewProps} />
    </FormBuilderContext.Provider>
  );
};

const mapStateToProps = (state, { pageId, formsRoute }) => {
  const pathname = state.router.location.pathname;

  const notOnThisPage = page =>
    // slice from 1 to skip leading slash
    page.slice(1, pageId.length + 1) !== pageId;

  let previousLocation =
    // get most recent page that is not a form builder page
    state.state.history
      // don't mutate original
      .slice()
      .reverse()
      .find(notOnThisPage) ||
    // default to the forms dashboard page
    formsRoute;

  const formId = pathname.split('/').slice(1)[1];
  const form = state.state.formBuilder[formId] || null;

  return {
    pathname,
    formId,
    previousLocation,
    form
  };
};

export default connect(mapStateToProps)(FormBuilderEngine);
